import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { encodeGeohash } from "./geohash";

export function generateOfficeId() {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 9);
}

// Root-level location fields mirror the primary office, for readers that
// predate the offices array (nearby-dealer queries, the app's dealer cards).
export function syncPrimaryToRoot(offices) {
  const primary = offices.find((o) => o.isPrimary) || offices[0];
  if (!primary) {
    return {
      latitude: 0,
      longitude: 0,
      geohash: "",
      address: "",
      locality: "",
      city: "",
      state: "",
    };
  }
  return {
    latitude: primary.latitude,
    longitude: primary.longitude,
    geohash: primary.geohash,
    address: primary.address,
    locality: primary.locality,
    city: primary.city,
    state: primary.state,
  };
}

// The offices array for a dealer doc saved before offices existed — from the
// legacy `locations` list, else the root coordinates. null when the doc
// already has offices or has nothing to convert.
function buildOfficesFromLegacy(data) {
  if (Array.isArray(data.offices) && data.offices.length > 0) return null;

  if (Array.isArray(data.locations) && data.locations.length > 0) {
    return data.locations.map((loc, idx) => ({
      id: generateOfficeId(),
      officeName: idx === 0 ? "Main Office" : `Branch ${idx + 1}`,
      address: loc.address || "",
      locality: loc.locality || "",
      city: loc.city || "",
      state: loc.state || "Karnataka",
      latitude: loc.latitude || 12.9716,
      longitude: loc.longitude || 77.5946,
      geohash: loc.geohash || encodeGeohash(loc.latitude || 12.9716, loc.longitude || 77.5946, 9),
      contactNumber: loc.contactNumber || data.contactNumber || data.phoneNumber || "",
      isPrimary: idx === 0,
    }));
  }

  if (typeof data.latitude === "number" && typeof data.longitude === "number") {
    return [
      {
        id: generateOfficeId(),
        officeName: "Main Office",
        address: data.address || "",
        locality: data.locality || "",
        city: data.city || "",
        state: data.state || "Karnataka",
        latitude: data.latitude,
        longitude: data.longitude,
        geohash: data.geohash || encodeGeohash(data.latitude, data.longitude, 9),
        contactNumber: data.contactNumber || data.phoneNumber || "",
        isPrimary: true,
      },
    ];
  }

  return null;
}

/**
 * Converts a legacy dealer doc to the offices array and saves it. Runs at
 * portal sign-in (App.js), so Settings and Listings see the offices without
 * the Offices tab having been opened first. Returns the dealer's offices.
 */
export async function migrateLegacyOffices(uid, data) {
  const migrated = buildOfficesFromLegacy(data);
  if (!migrated) return Array.isArray(data.offices) ? data.offices : [];

  await setDoc(
    doc(db, "users", uid),
    { offices: migrated, ...syncPrimaryToRoot(migrated), updatedAt: serverTimestamp() },
    { merge: true }
  );
  return migrated;
}
